import { useHistory } from 'react-router-dom'
import { useTranslation } from 'contexts/Localization'
import { Flex, IconButton, Text, Link, ChevronLeftIcon } from 'vvs-uikit'
import { Rarity, Tier } from 'state/vvsgotchi/models'
import Collapse from 'components/Collapse/Base'
import Row from 'components/Layout/Row'
import PixelatedImage from 'views/VvsGotchi/components/PixelatedImage'
import { formatUrlSearch } from 'utils/urlSearch'
import { Search } from 'config/constants/paths'

import { rarityTierMatrix, rarityToName, tierRangeConfig, tierToIcon, tierToName } from '../config'
import {
  Page,
  FaqContent,
  FaqTitle,
  QuestionText,
  AnswerUl,
  AnswerSubUl,
  Hr,
  StyledRow,
  StyledTable,
  StyledGrid,
  StyledImage,
} from './styles'

const LeaderboardFAQ: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()

  return (
    <Page>
      <FaqContent pl="15px" pt={2} zIndex={1} overflowY="auto" maxHeight="100vh">
        <IconButton
          my={3}
          scale="xs"
          variant="text"
          onClick={() => history.replace({ search: formatUrlSearch(Search.VVS_GOTCHI_LEADERBOARD) })}
          aria-label="back to previous page"
        >
          <ChevronLeftIcon width="32px" color="black" />
        </IconButton>
        <FaqTitle mt={2} as="h1">
          {t('Leaderboard FAQ')}
        </FaqTitle>

        <Collapse title={<QuestionText>{t('What is the leaderboard for?')}</QuestionText>} arrowColor="black">
          <AnswerUl>
            <li>{t('The leaderboard displays each of your Miner Mole’s ranking vs peers')}</li>
            <li>{t('Only Moles with more than 0 EXP will appear on the leaderboard')}</li>
            <li>{t('Peers are defined as other Miner Moles within the same rarity level as yours')}</li>
            <li>{t('Your Mole’s leaderboard ranking determines its farm boost multiplier')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('How can I determine my Mole’s farm boost multiplier?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>{t('Please refer to the table below:')}</li>
            <RankTable />
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse title={<QuestionText>{t('What do tiers mean?')}</QuestionText>} arrowColor="black">
          <AnswerUl>
            <li>{t('Depending on which tier your Mole falls within, this will determine their farm boost factor')}</li>
            <li>{t('Rankings are broken down into 3 tiers, as shown in the table above')}</li>
            <AnswerSubUl>
              <li>{t('Miner God: the Top 30% ranked Moles')}</li>
              <li>{t('Miner Master: Moles ranked within the mid 30-70%')}</li>
              <li>{t('Junior Miner: Bottom 30% ranked Moles')}</li>
            </AnswerSubUl>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse title={<QuestionText>{t('How do I increase my ranking?')}</QuestionText>} arrowColor="black">
          <AnswerUl>
            <li>{t('All Moles are ranked by amount of total Experience Points (EXP) earned')}</li>
            <li>{t('You earn EXP by going on Quests, which are available on the “Quest” page ')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('How often does the leaderboard get updated?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>{t('Leaderboard rankings get updated in real time')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('When will the change in my boost multiplier take effect?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>
              {t(
                'The change in boost multiplier will be applied to your staked Crystal farm once a week -  it will happen every Wednesday at 00:00 UTC',
              )}
            </li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={
            <QuestionText>
              {t(
                'My Mole has the same total EXP score as another peer, however I am ranked higher/lower. Why is this so?',
              )}
            </QuestionText>
          }
          arrowColor="black"
        >
          <AnswerUl>
            <li>
              {t(
                'For Moles with the same total EXP, the Mole who reached that level of EXP first will be ranked ahead of the others',
              )}
            </li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('Why hasn’t my Mole’s EXP increased after completing a Quest?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>
              {t(
                'If your Mole(s) did not earn any EXP after you have completed a quest, there may be two reasons for this:',
              )}
            </li>
            <AnswerSubUl>
              <li>
                {t(
                  'The Mole is not on your team. Add the Mole to your team so it can also start earning EXP when you go on quests!',
                )}
              </li>
              <li>
                {t(
                  'The Mole had previously completed a quest in one wallet before being transferred to another wallet during the same day. If you would like your Mole to earn EXP while in the new wallet, you will have to wait till the end of the day (defined as 00:00 UTC) to start a quest. ',
                )}
              </li>
            </AnswerSubUl>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse title={<QuestionText>{t('Why isn’t my Mole on the leaderboard?')}</QuestionText>} arrowColor="black">
          <AnswerUl>
            <li>
              {t(
                'Only Moles that have earned EXP will be added to the leaderboard. This may explain why your Mole that has 0 EXP is not included.',
              )}
            </li>
          </AnswerUl>
        </Collapse>
        <Text color="black" fontSize="13px" mt="100px" mb="40px">
          {t('For more information, click ')}
          <Link
            inline
            underline="always"
            bold
            href="https://docs.vvs.finance/product-guides/vvs-gotchi/leaderboard"
            target="_blank"
          >
            {t('here')}
          </Link>
          .
        </Text>
      </FaqContent>
    </Page>
  )
}

export default LeaderboardFAQ

const headerWidthConfig: Record<Tier, string> = {
  [Tier.JUNIOR]: '25px',
  [Tier.MASTER]: '26.34px',
  [Tier.GOD]: '38.5px',
}
const RankTable = () => {
  const { t } = useTranslation()
  return (
    <>
      <StyledGrid gridTemplateColumns="0.85fr 3.15fr" mt={4} mb={3}>
        <Flex> </Flex>
        <Flex flexDirection="column">
          <Flex mb={3}>
            <StyledImage src="/images/arrow-long.png" alt="table rank trend indicator arrow" />
          </Flex>
          <Row padding="2px 15px 2px 0" justify="space-between">
            <Text color="textSubtle" fontSize="11px">
              {t('Lowest EXP')}
            </Text>
            <Text color="textSubtle" fontSize="11px">
              {t('Highest EXP')}
            </Text>
          </Row>
        </Flex>
      </StyledGrid>
      <StyledTable>
        <thead>
          <StyledRow>
            <th> </th>
            {Object.keys(Tier).map((tier) => {
              const [w, rangeTranslateFn, iconSrc] = [headerWidthConfig[tier], tierRangeConfig[tier], tierToIcon[tier]]
              if (w && rangeTranslateFn && iconSrc) {
                return (
                  <th key={tier}>
                    <PixelatedImage src={iconSrc} width={w} />
                    <Text color="black" fontSize="13px" bold>
                      {tierToName[tier](t).full}
                    </Text>
                    <Text color="blue" fontSize="11px">
                      {rangeTranslateFn(t)}
                    </Text>
                  </th>
                )
              }
              return null
            })}
          </StyledRow>
        </thead>
        <tbody>
          {Object.keys(Rarity).map((r) => {
            const metricsByTier = rarityTierMatrix[r]
            if (metricsByTier) {
              return (
                <StyledRow key={r}>
                  <td>
                    <Text color="black" fontSize="13px">
                      {rarityToName?.[r]?.(t)}
                    </Text>
                  </td>
                  {Object.keys(metricsByTier).map((tier) => {
                    const tierInfo = metricsByTier[tier]
                    return (
                      <td key={tier}>
                        <Text color="black" fontSize="20px" bold>
                          {tierInfo.boost}x
                        </Text>
                        <Text color="blue" fontSize="11px">
                          {t('Rank %rangeStart%-%rangeEnd%', tierInfo)}
                        </Text>
                      </td>
                    )
                  })}
                </StyledRow>
              )
            }
            return null
          })}
        </tbody>
      </StyledTable>
    </>
  )
}
